import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField } from '@mui/material';
import { Refresh, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from 'react-toastify';
import { InvalidMessage, GeneralError, accountInputLen, memberInputLen } from "../../Utility/Constants";
import Table from '../../Table/Table';
import { PAPriceDetailColumns } from '../../Table/TableColumn';
import { tableColumns } from '../../Redux/TableSlice';
import { currentData, clearData, updateSearch, searchClicked, updateInputError } from '../../Redux/MNR/PAPriceDetailSlice';
import { clearAll, searchTriggered } from '../../Redux/CNS/CommonSlice';
import {
    validateRegexWithNumbers, validateRegexAlphaNumeric, validateRegexNumbersCharSpecialCharLong
} from '../../Validation/Validation';
import Spinner from '../../Spinner/Spinner';
import { showAll, isMNR } from '../../Redux/MNR/MNRCommonSlice';

function PAPriceDetail() {
    const dispatch = useDispatch();
    const error = useSelector(state => state.paPrice.error);
    const isSearched = useSelector(state => state.paPrice.isSearchClicked);
    const searchTriggeredValue = useSelector(state => state.cnsCommon.searchTriggered);
    const inputError = useSelector(state => state.paPrice.inputError);
    const hasAnyError = Object.values(inputError).some(value => value === true);
    const data = useSelector(state => state.paPrice.data);
    const isSearchEnable = ((data.Account && data.Account.trim() !== '') ||
        (data.GroupCode && data.GroupCode.trim() !== '') ||
        (data.AccountNumber && data.AccountNumber.trim() !== '') ||
        (data.MemberID && data.MemberID.trim() !== '') ||
        (data.PriorAuthorizationNumber && data.PriorAuthorizationNumber.trim() !== '') ||
        (data.CarrierCode && data.CarrierCode.trim() !== ''));
    const loading = useSelector(state => state.paPrice.loading);
    const showAllValue = useSelector(state => state.mnrCommon.showAll);

    useEffect(() => {
        if (error) {
            toast.error(GeneralError);
            return;
        }
    }, [error]);

    const handleResetInitial = (useCallback(() => {
        dispatch(clearAll());
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
        dispatch(isMNR(true));
    }, [dispatch]))

    const handleReset = () => {
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
    }

    useEffect(() => {
        handleResetInitial();
        dispatch(tableColumns(PAPriceDetailColumns));
    }, [dispatch, handleResetInitial])

    const handleChange = (name, value) => {
        const val = value.target.value || value.target.innerText;
        switch (name) {
            case 'PANumber':
                const updateRegexWithNumber = validateRegexNumbersCharSpecialCharLong(name, val, inputError);
                dispatch(updateInputError(updateRegexWithNumber));
                break;
            case 'Account':
                const updateRegexIncludeNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexIncludeNumbers));
                break;
            case 'Group':
                const updateRegexInclNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexInclNumbers));
                break;
            case 'Carrier':
                const updateRegexCarrierAlphaNumeric = validateRegexAlphaNumeric({
                    name,
                    value: val,
                    inputError,
                    maxLength: accountInputLen
                });
                dispatch(updateInputError(updateRegexCarrierAlphaNumeric));
                break;
            case 'MemberID':
                const updateRegexMemberAlphaNumeric = validateRegexAlphaNumeric({
                    name,
                    value: val,
                    inputError,
                    maxLength: memberInputLen
                });
                dispatch(updateInputError(updateRegexMemberAlphaNumeric));
                break;
            default:
                break;
        }

        const updatedData = {
            ...data,
            [name]: val
        };
        dispatch(currentData(updatedData));
    }

    const handleSearch = () => {
        dispatch(showAll(false));
        dispatch(searchClicked(true));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    const handleShowAll = () => {
        dispatch(showAll(true));
        dispatch(clearData());
        dispatch(searchClicked(false));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    return (
        <>
            {loading && <Spinner />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="memberID"
                        label="MemberID"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('MemberID', e)}
                        value={data.MemberID}
                        error={inputError.MemberID}
                        helperText={inputError.MemberID ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="carrier"
                        label="Carrier"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('CarrierCode', e)}
                        value={data.CarrierCode}
                        error={inputError.CarrierCode}
                        helperText={inputError.CarrierCode ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="account"
                        label="Account"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('AccountNumber', e)}
                        value={data.AccountNumber}
                        error={inputError.AccountNumber}
                        helperText={inputError.AccountNumber ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="group"
                        label="Group"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('GroupCode', e)}
                        value={data.GroupCode}
                        error={inputError.GroupCode}
                        helperText={inputError.GroupCode ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="paNumber"
                        label="PANumber"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('PriorAuthorizationNumber', e)}
                        value={data.PriorAuthorizationNumber}
                        error={inputError.PriorAuthorizationNumber}
                        helperText={inputError.PriorAuthorizationNumber ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ justifyContent: 'center', paddingTop: '24px' }}>
                <Button
                    variant="contained"
                    onClick={handleShowAll}
                >
                    Show All
                </Button>
                <Button
                    variant={(!isSearched && !showAllValue) ? "contained" : "outlined"}
                    style={isSearched ? { color: 'var(--primary-font-color)' } : undefined}
                    size="small"
                    onClick={handleReset}
                    endIcon={<Refresh />}
                    disabled={!isSearched && !showAllValue}
                >
                    Reset
                </Button>
                <Button
                    variant={(!isSearchEnable || hasAnyError) ? "contained" : "contained"}
                    size="large"
                    onClick={handleSearch}
                    endIcon={<Search />}
                    style={(isSearchEnable && !hasAnyError) ? { color: 'var(--primary-font-color)', background: 'var(--primary-color)', border: '1px solid var(--primary-font-color)' } : undefined}
                    disabled={!isSearchEnable || hasAnyError}
                >
                    Search
                </Button>
            </Grid>
            {((isSearched) || (showAllValue)) && <Table />}
        </>
    )
}

export default PAPriceDetail;
